import { useEffect, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useEditSpaceGroupModal } from "@circle-react/components/Modals/EditSpaceGroupModal";
import { useSpaceGroupsContext } from "@circle-react/contexts";
import type { SpaceGroup } from "@circle-react/types/SpaceGroup";

/**
 * Handles opening the edit space group modal when visiting /s/:slug/edit
 * Also handles navigating back to /s/:slug when the modal closes
 */
export const useEditSpaceGroupModalFromUrl = () => {
  const location = useLocation();
  const history = useHistory();
  const params = useParams<{ spaceGroupSlug?: string }>();
  const spaceGroupSlug = params.spaceGroupSlug;

  const spaceGroupsContext = useSpaceGroupsContext();
  const { findBy, isDataLoaded } = spaceGroupsContext.helpers;
  const editSpaceGroupModal = useEditSpaceGroupModal();

  const editSpaceGroupPathRegex = /^\/s\/[^/]+\/edit$/;
  const isMatch = editSpaceGroupPathRegex.exec(location.pathname) !== null;
  const hasOpenedRef = useRef(false);
  const prevVisibleRef = useRef(false);

  // Opening Logic
  useEffect(() => {
    if (!isMatch) {
      hasOpenedRef.current = false;
      return;
    }

    if (
      isMatch &&
      isDataLoaded() &&
      !!spaceGroupSlug &&
      !hasOpenedRef.current
    ) {
      hasOpenedRef.current = true;
      const spaceGroup = findBy({ slug: spaceGroupSlug }) as SpaceGroup;
      if (spaceGroup) {
        void editSpaceGroupModal.show({
          spaceGroup,
        });
      }
    }
  }, [
    location.pathname,
    isMatch,
    isDataLoaded,
    spaceGroupSlug,
    findBy,
    editSpaceGroupModal,
  ]);

  // Cleanup Logic - Navigate back when modal closes
  useEffect(() => {
    if (
      prevVisibleRef.current &&
      !editSpaceGroupModal.visible &&
      isMatch &&
      spaceGroupSlug
    ) {
      hasOpenedRef.current = false;
      history.replace(`/s/${spaceGroupSlug}`);
    }
    prevVisibleRef.current = editSpaceGroupModal.visible;
  }, [editSpaceGroupModal.visible, isMatch, spaceGroupSlug, history]);
};
